/**
 * adminModules/index.js
 *
 * Catalog of the admin web modules that the Manager app mirrors page-for-page
 * (admin `src/config/menuStructure.js`). Each group file (`crm`, `warehouse`,
 * `marketing`, `hr`, `system`, `service`) default-exports an array of module definitions;
 * this index aggregates them. PURE DATA — no React / React Native imports, so
 * `helpers/deepLink/adminPathMapping.js` can derive deep-link entries from it.
 *
 * Module definition:
 * @typedef {object} AdminModuleDef
 * @property {string} id          Unique module id (e.g. 'marketing').
 * @property {string} sectionId   Modules-hub section: 'sales' | 'main' | 'functional' | 'general'.
 * @property {string} accessKey   `managementAccess` rule key gating the whole module.
 * @property {string} titleKey    i18n key (`adminmod_<group>_<id>_title`).
 * @property {string} subtitleKey i18n key.
 * @property {string} icon        MaterialCommunityIcons glyph.
 * @property {string} tone        ManagementUI tone: info | success | warning | violet | primary | danger.
 * @property {string} adminPath   Admin landing path of the module (web fallback), e.g. '/marketing/main'.
 * @property {AdminPageDef[]} pages Every sub-page of the admin menu group, in admin order.
 *
 * Page definition:
 * @typedef {object} AdminPageDef
 * @property {string} id          Unique within the module.
 * @property {string} titleKey    i18n key.
 * @property {string} icon        MaterialCommunityIcons glyph.
 * @property {string} adminPath   The admin route (exactly as in the admin menu `link`). Always set:
 *                                it is the AdminWebScreen fallback AND the deep-link pattern.
 * @property {string} [screen]    Native stack route (registered through `<group>.routes.js`). When set, the page opens natively.
 * @property {object} [params]    Route params for `screen`.
 * @property {string} [list]      ManagementListScreen `type` (alternative to `screen`).
 * @property {string} [accessKey] Per-page `managementAccess` key (defaults to the module's).
 *
 * A page with neither `screen` nor `list` opens the authenticated admin page in
 * AdminWebScreen — a real, working page, never a placeholder.
 */

import crm from './crm';
import warehouse from './warehouse';
import marketing from './marketing';
import hr from './hr';
import system from './system';
import service from './service';

/** @type {AdminModuleDef[]} */
export const ADMIN_MODULES = [...crm, ...warehouse, ...marketing, ...hr, ...system, ...service];

/**
 * @param {string} id Module id.
 * @returns {AdminModuleDef|undefined} The module definition.
 */
export const getAdminModule = (id) => ADMIN_MODULES.find((mod) => mod.id === id);

export default ADMIN_MODULES;
